import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { DemandPlanVariant } from "@models/DemandPlanVariant";
import client, { RequestParams, useMutateError } from "@services/api";
import { buildPaginatedQuery } from "@utils/reactQuery";

import { demandPlanQueryKeyFactory } from "./demandPlanQueries";

const apiRoot = "/demand-plan-variants";

export const demandPlanVariantQueryKeyFactory = createQueryKeys(
  "demandPlanVariants",
  {
    detail: (id: string) => ({
      queryKey: [id],
      queryFn: () =>
        client
          .get<DemandPlanVariant>(`${apiRoot}/${id}`)
          .then((res) => res.data),
    }),
    list: (params) => ({
      queryKey: [params],
      queryFn: () =>
        client
          .get<DemandPlanVariant[]>(apiRoot, {
            params,
          })
          .then((res) => res.data),
    }),
    paginated: (params) => ({
      queryKey: [params],
      queryFn: () => client.get<DemandPlanVariant[]>(`${apiRoot}`, { params }),
    }),
  }
);

export const useDemandPlanVariantListQuery = (params?: RequestParams) => {
  return useQuery({
    ...demandPlanVariantQueryKeyFactory.list({
      skipPagination: true,
      ...params,
    }),
    placeholderData: keepPreviousData,
  });
};

//TODO still export this or remove it?
export const useDemandPlanVariantQuery = (id: string | null | undefined) => {
  return useQuery({
    ...demandPlanVariantQueryKeyFactory.detail(id!),
    enabled: !!id,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

export const usePaginateDemandPlanVariantQuery = buildPaginatedQuery(
  demandPlanVariantQueryKeyFactory.paginated,
  {
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  }
);

type UpdatePayload = { id: string };

type DemandPlanVariantStatusPayload = UpdatePayload & {
  status: string;
};

type DemandPlanVariantOrderPayload = UpdatePayload & {
  toOrder: string;
};

type DemandPlanVariantPayload =
  | DemandPlanVariantStatusPayload
  | DemandPlanVariantOrderPayload;

export const useUpdateDemandPlanVariantMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: DemandPlanVariantPayload) =>
      client
        .put<DemandPlanVariant>(`${apiRoot}/${id}`, {
          __type: "demand-plan-variant",
          ...payload,
        })
        .then((res) => res.data),
    onSuccess: (data, { id }) => {
      queryClient.setQueriesData<DemandPlanVariant[]>(
        {
          queryKey: demandPlanVariantQueryKeyFactory.list._def,
        },
        (dpvs) => dpvs?.map((dpv) => (dpv.id === id ? data : dpv))
      );

      queryClient.invalidateQueries({
        queryKey: demandPlanQueryKeyFactory.detail(data.demandPlan.id)._ctx
          .summary.queryKey,
      });

      return queryClient.setQueryData(
        demandPlanVariantQueryKeyFactory.detail(id).queryKey,
        data
      );
    },
    onError: (error) => {
      setError(error, "Update Demand Plan Item");
    },
  });
};
